import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Share2, Copy, Link2Off, ExternalLink, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const btn = 'inline-flex items-center gap-1 h-7 px-2 rounded text-[11px] border disabled:opacity-40';

/** V15.57 — Lien public révocable vers la visionneuse client (/viewer/:token). */
export const ViewerShareButton = ({ projectId }) => {
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/projects/${projectId}/viewer-share`, opts); setShare(r.data.share); } catch { setShare(null); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const url = share ? `${window.location.origin}${share.public_url}` : null;
  const copy = async (u) => { try { await navigator.clipboard.writeText(u); toast.success('Lien copié'); } catch { window.prompt('Copiez le lien', u); } };
  const create = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/projects/${projectId}/viewer-share`, {}, opts); setShare(r.data); await copy(`${window.location.origin}${r.data.public_url}`); setOpen(true); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Partage impossible'); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm('Désactiver le lien ? Le client ne pourra plus ouvrir la visionneuse.')) return;
    setBusy(true);
    try { await axios.delete(`${API}/projects/${projectId}/viewer-share`, opts); setShare(null); setOpen(false); toast.success('Lien désactivé'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Révocation impossible'); }
    finally { setBusy(false); }
  };
  return (
    <span className="relative inline-flex items-center gap-1" data-testid="viewer-share">
      <button type="button" onClick={() => (share ? setOpen((o) => !o) : create())} disabled={busy}
              className={`${btn} ${share ? 'border-emerald-400/50 text-emerald-200' : 'border-amber-300/50 text-amber-200 hover:bg-amber-400/10'}`}
              title={share ? 'Lien client actif — options' : 'Créer un lien public vers la visionneuse 3D (rotation, coupe d\'étage, rendus texturés)'} data-testid="viewer-share-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} {share ? 'Visionneuse partagée' : 'Partager la visionneuse'}
      </button>
      {open && share && (
        <div className="absolute right-0 top-full mt-1 z-[60] w-80 rounded-lg border p-2 space-y-1.5 text-[11px] shadow-xl" style={{ background: '#0d0d10', borderColor: 'var(--c-border)' }} data-testid="viewer-share-popover">
          <div className="text-gray-400">Lien public (lecture seule) · {share.views || 0} ouverture{(share.views || 0) > 1 ? 's' : ''}</div>
          <input readOnly value={url} onFocus={(e) => e.target.select()} className="w-full bg-white/[0.06] border border-white/10 rounded px-1.5 py-1 text-gray-100 text-[10.5px]" data-testid="viewer-share-url" />
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => copy(url)} className={`${btn} border-white/15 text-gray-200 hover:bg-white/5`} data-testid="viewer-share-copy"><Copy className="w-3 h-3" /> Copier</button>
            <a href={url} target="_blank" rel="noreferrer" className={`${btn} border-white/15 text-gray-200 hover:bg-white/5`} data-testid="viewer-share-open"><ExternalLink className="w-3 h-3" /> Ouvrir</a>
            <button type="button" onClick={revoke} className={`${btn} ml-auto border-rose-400/40 text-rose-300 hover:bg-rose-500/10`} data-testid="viewer-share-revoke"><Link2Off className="w-3 h-3" /> Désactiver</button>
          </div>
        </div>
      )}
    </span>
  );
};

export default ViewerShareButton;
